
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "@/hooks/use-toast";

export const useSyncLocalData = () => {
  const { user } = useAuth();
  
  useEffect(() => {
    if (!user) return;
    
    const syncLocalHabits = async () => {
      try {
        // Get local habits
        const localHabitsRaw = localStorage.getItem('habits');
        if (!localHabitsRaw) return;
        
        const localHabits = JSON.parse(localHabitsRaw);
        if (!Array.isArray(localHabits) || localHabits.length === 0) return;
        
        // Check if user already has habits in Supabase
        const { data: existingHabits, error: checkError } = await supabase
          .from('habits')
          .select('id')
          .limit(1);
          
        if (checkError) throw checkError;
        
        // Only import if no habits exist yet
        if (existingHabits && existingHabits.length > 0) return;
        
        // Map local habits to Supabase format
        const habitsToImport = localHabits.map((habit: any) => ({
          user_id: user.id,
          title: habit.title,
          emoji: habit.emoji,
          total_days: habit.totalDays,
          completed_days: habit.completedDays,
          reward: habit.reward,
          reward_image: habit.rewardImage,
          priority: habit.priority || 'medium',
          is_completed: habit.isCompleted,
          category: habit.category || [],
          daily_target: habit.dailyTarget,
          daily_progress: habit.dailyProgress || 0,
        }));
        
        // Import to Supabase
        const { error: importError } = await supabase
          .from('habits')
          .insert(habitsToImport);
          
        if (importError) throw importError;
        
        toast({
          title: "Data imported",
          description: "Your local habits have been imported to your account",
        });
      } catch (error: any) {
        console.error("Error importing local habits:", error);
      }
    };
    
    const syncLocalCategories = async () => {
      try {
        // Get local categories
        const localCategoriesRaw = localStorage.getItem('habit-tracker-categories');
        if (!localCategoriesRaw) return;
        
        const localCategories = JSON.parse(localCategoriesRaw);
        if (!Array.isArray(localCategories) || localCategories.length === 0) return;
        
        // Check if user already has custom categories in Supabase
        const { data: existingCategories, error: checkError } = await supabase
          .from('categories')
          .select('id')
          .limit(1);
          
        if (checkError) throw checkError;
        
        // Only import if no custom categories exist yet
        if (existingCategories && existingCategories.length > 0) return;
        
        // Filter out default categories that are hardcoded in constants
        const categoriesFromConstants = (await import('@/constants/categories')).CATEGORIES;
        const defaultCategoryNames = categoriesFromConstants.map(c => c.name);
        
        const customCategories = localCategories.filter(
          (cat: any) => !defaultCategoryNames.includes(cat.name)
        );
        
        if (customCategories.length === 0) return;
        
        // Map local categories to Supabase format
        const categoriesToImport = customCategories.map((category: any) => ({
          user_id: user.id,
          name: category.name,
          description: category.description,
          icon_src: category.iconSrc,
        }));
        
        // Import to Supabase
        const { error: importError } = await supabase
          .from('categories')
          .insert(categoriesToImport);
          
        if (importError) throw importError;
      } catch (error: any) {
        console.error("Error importing local categories:", error);
      }
    };
    
    // Run the sync once when user logs in
    syncLocalHabits();
    syncLocalCategories();
  }, [user]);
  
  return null;
};
